import { Fragment, useEffect, useState } from 'react';
import { type IProduct, type Nullable } from '@recargas-dominicanas/core/types';
import { useAsyncEffect } from '@recargas-dominicanas/core/utils';
import { ProductsApi } from '@recargas-dominicanas/core/api';
import { productCategoryList, productKeyList, productTypeList } from '@recargas-dominicanas/core/constants';
import { useProductStore } from '../../../store/productStore';
import { Modal } from '../../../components/modal/modal.component';
import { ImageField } from '../../../components/image-field/image-field.component';
import { useForm } from '@recargas-dominicanas/core/hooks';
import { minLength, required } from '@recargas-dominicanas/core/utils/formValidators';

import { 
  Autocomplete,
  FieldError,
  Icon,
  OptionIcon,
  Select, 
  SelectOption, 
  TextArea, 
  TextField 
} from '@recargas-dominicanas/core/components';

import { style } from './edit-product-modal.module.css';

interface Props {
  open: boolean;
  product: IProduct;
  onClose: () => void;
}

export function EditProductModal(props: Props) {

  const { open, product, onClose } = props;

  const productStore = useProductStore();

  const [priceFieldValue, setPriceFieldValue] = useState('');
  const [disabledButton, setDisabledButton] = useState(false);
  const [productKeys, setProductKeys] = useState(productKeyList);
  const [error, setError] = useState<string>();

  const form = useForm({
    name: product.name,
    image: { value: undefined as File | undefined, optional: true },
    key: product.key as Nullable<IProduct['key']>,
    type: product.type as Nullable<IProduct['type']>,
    category: product.category as Nullable<IProduct['category']>,
    profitType: { value: product.profitType as Nullable<IProduct['profitType']> },
    profit: product.profit.toString(),
    min: { value: product.min?.toString() ?? '', optional: true },
    max: { value: product.max?.toString() ?? '', optional: true },
    instructions: { value: product.pin?.instructions ?? '', optional: true },
    prices: { value: product.pin?.prices.map(price => price.price) ?? [], optional: true }
  }, [open]);

  useAsyncEffect(async () => {
    if (!product.image) {
      form.image.addValidator(required);
    }
  }, [open, product.image]);

  useEffect(() => {

    if (product.type === 'Recarga') {
      form.min.addValidator(required);
      form.max.addValidator(required);
    } else {
      form.min.removeValidator(required);
      form.max.removeValidator(required);
    }

    if (product.type === 'Pin') {
      form.instructions.addValidator(required);
      form.prices.addValidator(minLength(1, 'Campo invalido'));
    } else {
      form.instructions.removeValidator(required);
      form.prices.removeValidator(minLength.key);
    }
  }, [open, product.type]);

  function handleAddPrice() {
    if (priceFieldValue !== '') {
      const prices = Array.from(new Set([...form.prices.value, parseInt(priceFieldValue)]));
      form.prices.update(prices);
      form.prices.validate(prices);
      setPriceFieldValue('');
    }
  }

  function handleRemovePrice(price: number) {
    form.prices.update(form.prices.value.filter(item => item !== price));
  }

  async function handleSave() {

    if (form.validate()) {

      try {

        setDisabledButton(true);

        await ProductsApi.update(product.id, {
          name: form.name.value,
          image: form.image.value!,
          key: form.key.value!, 
          type: form.type.value!,
          category: form.category.value!,
          profitType: form.profitType.value!,
          profit: parseFloat(form.profit.value),
          min: product.type === 'Recarga' ? parseInt(form.value.min) : undefined,
          max: product.type === 'Recarga' ? parseInt(form.value.max) : undefined,
          instructions: form.instructions.value.trim() !== '' ? form.instructions.value : undefined,
          prices: form.prices.value.map(price => ({ price }))
        });

        await productStore.fetch();
  
        setDisabledButton(false);

        handleClose();

      } catch (err) {

        console.error(err);
        setError('Error actualizando producto');
        setDisabledButton(false);
      }
    }
  }

  function handleClose() {
    onClose();
    setDisabledButton(false);
    setError(undefined);
    form.clear();
  }

  async function handleSearch(name = '') {
    try {
      setProductKeys(productKeyList.filter(item => item.value.toLowerCase().includes(name.toLowerCase())));
    } catch (err) {
      console.error(err);
    }
  }

  return (
    <Modal
      open={open}
      style={style.modal}
      title='Editar producto'
      error={error}
      secondaryButton='Guardar'
      disabledSecondaryButton={disabledButton}
      onClose={handleClose}
      onSecondaryClick={handleSave}>
      <TextField 
        style={style.textField} 
        label='Nombre'
        value={form.name.value}
        error={form.name.error}
        onChange={form.name.onChange}
        onBlur={form.name.onBlur}/>
      <ImageField
        className={style.imageField}
        label='Logo'
        image={product.image}
        name={product.image}
        placeholder='Subir imagen'
        field={form.image}/>
      <Autocomplete
        style={style.autocomplete}
        label='Clave'
        placeholder='Seleccionar clave'
        hideIcon={true}
        value={form.key.value}
        error={form.key.error}
        onChange={form.key.onChange}
        onSearch={handleSearch}>
        {productKeys.map(productKey => (
          <SelectOption
            key={productKey.key} 
            value={productKey.key} 
            label={productKey.value}>
            {productKey.value}
          </SelectOption>
        ))}
      </Autocomplete>
      <Select 
        style={style.select} 
        label={'Tipo'}
        placeholder='Seleccionar tipo'
        value={form.type.value}
        error={form.type.error}
        onChange={(value) => {
          form.type.onChange(value);
          form.type.validate(value);
        }}>
        {productTypeList.map(productType => (
          <SelectOption
            key={productType.key} 
            value={productType.key} 
            label={productType.value}>
            {productType.value}
          </SelectOption>
        ))}
      </Select>
      <Select 
        style={style.select} 
        label={'Categoria'}
        placeholder='Seleccionar categoria'
        value={form.category.value}
        error={form.category.error}
        onChange={(value) => {
          form.category.onChange(value);
          form.category.validate(value);
        }}>
        {productCategoryList.map(productCategory => (
          <SelectOption
            key={productCategory.key} 
            value={productCategory.key} 
            label={productCategory.value}>
            {productCategory.value}
          </SelectOption>
        ))}
      </Select>
      <Select
        style={style.select} 
        label='Tipo de beneficio'
        placeholder='Seleccionar tipo'
        value={form.profitType.value}
        error={form.profitType.error}
        onChange={(value) => {
          form.profitType.onChange(value);
          form.profitType.validate(value);
        }}>
        <SelectOption value='percent' label='Porciento'>Porciento</SelectOption>
        <SelectOption value='fixed' label='Fijo'>Fijo</SelectOption>
      </Select>
      <TextField 
        style={style.textField} 
        label='Beneficio'
        type='number'
        value={form.profit.value}
        error={form.profit.error}
        onChange={form.profit.onChange}
        onBlur={form.profit.onBlur}/>
      {product.type === 'Recarga' &&
        <Fragment>
          <TextField 
            style={style.textField} 
            label='Mínimo'
            type='number'
            value={form.min.value}
            error={form.min.error}
            onChange={form.min.onChange}
            onBlur={form.min.onBlur}/>
          <TextField 
            style={style.textField} 
            label='Máximo'
            type='number'
            value={form.max.value}
            error={form.min.error}
            onChange={form.max.onChange}
            onBlur={form.max.onBlur}/>
        </Fragment>
      }
      {product.type === 'Pin' &&
        <Fragment>
          <TextArea 
            style={style.textArea} 
            label='Instrucciones'
            value={form.instructions.value}
            error={form.instructions.error}
            onChange={form.instructions.onChange}
            onBlur={form.instructions.onBlur}/>
          <TextField 
            style={style.priceField} 
            type='number'
            label='Precios'
            placeholder='Agregar precio'
            value={priceFieldValue}
            onChange={value => setPriceFieldValue(value)}
            onEnter={handleAddPrice}
            rightIcon={
              <OptionIcon
                className={style.addPriceIcon}
                icon='add' 
                onClick={handleAddPrice}/>
            }/>
          {form.prices.value.length !== 0 &&
            <div className={style.prices}>
              {form.prices.value.map(price => 
                <div 
                  className={style.priceItem} 
                  key={price}>
                  <div className={style.priceValue}>{price}</div>
                  <Icon 
                    className={style.priceDeleteIcon}
                    icon='clear'
                    onClick={() => handleRemovePrice(price)}/>
                </div>
              )}
            </div>
          }
          <FieldError message={form.prices.error}/>
        </Fragment>
      }
    </Modal>
  );
}
